import { apiGet, apiPost, apiPostEmpty, apiPut, ApiError as ApiErrorForGuard } from './http';
import { apiUrl } from './config';
import { validateSession, validateCapabilities, validateCompanyPage, validateToolInventory, validateSyncHistory } from './validate';
import type { components, operations } from './generated/openapi';
import { RESPONSE_KEYS } from './guards';

/**
 * Wire types aliased from the GENERATED client (docs/openapi.yaml, pinned
 * sha256 773e4215…; see generated/PROVENANCE.md). Never hand-written for a
 * route that has a success schema; runtime-validated in validate.ts.
 */

export type AppRole = components['schemas']['AppRole'];
export type SessionView = components['schemas']['SessionView'];
export type CapabilityState = components['schemas']['CapabilityState'];
export type RuntimeCapability = components['schemas']['RuntimeCapability'];
export type ToolCapability = components['schemas']['ToolCapability'];
export type ProductCapabilities = components['schemas']['ProductCapabilities'];
export type ProductGate = components['schemas']['ProductCapabilityState'];
export type DerivedRetention = components['schemas']['DerivedRetentionContract'];
export type SystemCapabilities = components['schemas']['SystemCapabilities'];
export type CompanyPage = components['schemas']['CompanyPage'];
export type CompanyCatalogItem = components['schemas']['CompanyCatalogItem'];
export type CompanyToolInventory = components['schemas']['CompanyToolInventory'];
export type CompanyToolItem = components['schemas']['CompanyToolItem'];
export type ToolDisplay = components['schemas']['ToolDisplay'];
export type BucketRecord = components['schemas']['BucketRecord'];
export type RegisteredBucket = components['schemas']['RegisteredBucket'];
export type BucketPage = components['schemas']['BucketPage'];
export type RegisterBucketRequest = components['schemas']['RegisterBucketRequest'];
export type SyncRunSummary = components['schemas']['SyncRunSummary'];
export type SyncHistoryPage = components['schemas']['SyncHistoryPage'];
export type SyncRunStatus = components['schemas']['SyncRunStatus'];
export type SyncStartResult = components['schemas']['StartSyncRunResult'];

/** Envelope-data extractor for a generated operation's 200 response. */
export type Data<Op extends keyof operations> = operations[Op] extends {
  responses: { 200: { content: { 'application/json': { data: infer D } } } };
}
  ? D
  : never;

/** Light runtime guard: the response must be an object carrying every named key. */
function req<T>(route: string, v: unknown, keys: readonly string[]): T {
  if (typeof v !== 'object' || v === null) {
    throw new ContractViolation(route, 'body is not an object');
  }
  for (const k of keys) {
    if (!(k in (v as Record<string, unknown>))) throw new ContractViolation(route, `required key ${k} missing`);
  }
  return v as T;
}


/**
 * The packaging routes answer with AnyDataEnvelope in the pinned contract, so
 * no generated type describes them. The envelope's data IS the run (verified
 * against the deployed API), and req() proves the identifying keys are
 * present before any of it is read.
 */
export interface PackagingRunView {
  id: string;
  bucketId: string;
  state: string;
  factsRevision: number;
  requestedAt: string;
  stateChangedAt: string;
  deliveryRegion: string;
  supersededBy: string | null;
  supersededAt: string | null;
}

/**
 * Several methods below have no caller. They are the client side of routes the
 * backend serves but cannot yet fill: per-tool views, record and evidence
 * resolution, subgraphs, readiness assessments and the redacted log store all
 * read tables that no producer writes today. They are kept rather than deleted
 * so the surface stays one re-pin away from working, and named here so they
 * are not mistaken for dead code or wired up expecting an answer.
 */
export const backend = {
  getSession: async (signal?: AbortSignal) => validateSession(await apiGet<unknown>('/api/v1/session', signal)),
  getCapabilities: async (signal?: AbortSignal) =>
    validateCapabilities(await apiGet<unknown>('/api/v1/system/capabilities', signal)),
  listCompanies: async (pageSize?: number, cursor?: string, signal?: AbortSignal) => {
    const q = new URLSearchParams();
    if (pageSize) q.set('pageSize', String(pageSize));
    if (cursor) q.set('cursor', cursor);
    const qs = q.toString();
    return validateCompanyPage(await apiGet<unknown>(`/api/v1/companies${qs ? `?${qs}` : ''}`, signal));
  },
  /**
   * Every company, following the cursor to exhaustion.
   *
   * Same defect as the bucket catalogue had: one page of 200 was requested and
   * the cursor dropped, so past two hundred companies the picker, the rail and
   * every name lookup omitted the rest in silence. A company that is missing
   * from the list is not merely unlisted: every screen that resolves an id to
   * a name through it falls back to "Company".
   */
  listAllCompanies: async (signal?: AbortSignal): Promise<CompanyPage> => {
    const items: CompanyPage['items'] = [];
    let cursor: string | undefined;
    // Bounded runaway guard, not a limit: it reports itself if ever reached.
    const seen = new Set<string>();
    for (let page = 0; page < 50; page++) {
      const res = await backend.listCompanies(200, cursor, signal);
      items.push(...res.items);
      cursor = res.nextCursor ?? undefined;
      // Exhausted: no cursor, because there is genuinely nothing after this.
      if (!cursor) return { ...res, items, nextCursor: undefined };
      if (seen.has(cursor) || res.items.length === 0) return { ...res, items, nextCursor: cursor };
      seen.add(cursor);
    }
    // Cut short by the guard. The cursor is kept rather than cleared, so this
    // reads as "more exist" instead of claiming a complete list.
    return { items, nextCursor: cursor } as CompanyPage;
  },
  getCompanyTools: async (companyId: string, signal?: AbortSignal) =>
    validateToolInventory(await apiGet<unknown>(`/api/v1/companies/${encodeURIComponent(companyId)}/tools`, signal)),
  /**
   * One bucket's detections, with their grading.
   *
   * The company route is the only other source of a detection's `status` and
   * `evidence`, and it returns every bucket the company owns - so a session
   * working on one bucket received another's detections over the wire and
   * narrowed them in the browser. A different bucket is a different owner, and
   * the narrowing belongs on the server. Same item schema, so the same
   * validator reads both.
   */
  getBucketToolDetections: async (bucketId: string, signal?: AbortSignal) =>
    validateToolInventory(
      await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/tool-detections`, signal),
    ),
  /** One analysis run's live status and its frozen tool queue, by run id. */
  getAnalysisRun: async (runId: string, signal?: AbortSignal) =>
    req<Data<'getCompanyAnalysisRun'>>(
      'analysis run',
      await apiGet<unknown>(`/api/v1/company-analysis-runs/${encodeURIComponent(runId)}`, signal),
      RESPONSE_KEYS.analysisRun,
    ),
  /** Freezes THIS bucket's tool queue. Never another's. */
  startBucketAnalysis: (bucketId: string, idempotencyKey: string) =>
    apiPostEmpty<Data<'startBucketAnalysis'>>(
      `/api/v1/buckets/${encodeURIComponent(bucketId)}/analysis-runs`,
      idempotencyKey,
    ),
  listBuckets: async (pageSize?: number, cursor?: string, needle?: string, signal?: AbortSignal) => {
    const q = new URLSearchParams();
    if (pageSize) q.set('pageSize', String(pageSize));
    if (cursor) q.set('cursor', cursor);
    // Matched on the server. `_` and `%` are escaped as literals there, so a
    // search for either returns nothing rather than everything.
    if (needle) q.set('q', needle);
    const qs = q.toString();
    // The companies page is validated and this was not, so a malformed body
    // threw "undefined is not iterable" out of the exhaustion loop below: a
    // client-side type error, reported to the operator as a dead backend.
    return req<BucketPage>('buckets', await apiGet<unknown>(`/api/v1/buckets${qs ? `?${qs}` : ''}`, signal), ['items']);
  },
  /**
   * Every registered bucket, following the cursor to exhaustion.
   *
   * The catalogue mints a `nextCursor` whenever more exist, and every caller
   * asked for 200 and dropped it. Past two hundred buckets the navigation rail
   * and every scope picker simply omitted the rest, with nothing on screen
   * saying so. A page cap is a rendering decision; silently losing rows from a
   * catalogue is not.
   */
  listAllBuckets: async (signal?: AbortSignal, needle?: string): Promise<BucketPage> => {
    const items: BucketPage['items'] = [];
    let cursor: string | undefined;
    // A catalogue this size is bounded in practice; the cap is a runaway guard
    // rather than a limit, and it reports itself if it is ever reached.
    const seen = new Set<string>();
    for (let page = 0; page < 50; page++) {
      const res: BucketPage = await backend.listBuckets(200, cursor, needle, signal);
      items.push(...res.items);
      cursor = res.nextCursor ?? undefined;
      if (!cursor) return { ...res, items, nextCursor: null };
      // A cursor that does not advance would otherwise push the same page
      // fifty times and then report the result as truncated.
      if (seen.has(cursor) || res.items.length === 0) return { ...res, items, nextCursor: cursor };
      seen.add(cursor);
    }
    return { items, nextCursor: cursor ?? null } as BucketPage;
  },
  getBucket: (bucketId: string, signal?: AbortSignal) =>
    apiGet<RegisteredBucket>(`/api/v1/buckets/${encodeURIComponent(bucketId)}`, signal),
  listSyncRuns: async (bucketId: string, signal?: AbortSignal) =>
    validateSyncHistory(await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/sync-runs`, signal)),
  getSyncRun: async (syncRunId: string, signal?: AbortSignal) =>
    apiGet<SyncRunStatus>(`/api/v1/sync-runs/${encodeURIComponent(syncRunId)}`, signal),
  startSyncRun: (bucketId: string, idempotencyKey: string) =>
    apiPost<SyncStartResult>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/sync-runs`, {}, idempotencyKey),
  registerBucket: (body: RegisterBucketRequest, idempotencyKey: string) =>
    apiPost<RegisteredBucket>('/api/v1/buckets', body, idempotencyKey),
  /**
   * The AWS account's observed buckets, matched on the server.
   *
   * `q` is the difference between reading twelve rows and reading the whole
   * account on every keystroke. Without it the browser held the entire
   * catalogue to filter it, which is honest at hundreds and impossible at the
   * scale this is built for.
   */
  listAccountBuckets: async (
    companyId: string,
    q?: string,
    pageSize?: number,
    signal?: AbortSignal,
    cursor?: string,
  ) => {
    const p = new URLSearchParams({ companyId });
    if (q) p.set('q', q);
    if (pageSize) p.set('pageSize', String(pageSize));
    if (cursor) p.set('cursor', cursor);
    return req<Data<'listObservedAccountBuckets'>>(
      'account buckets',
      await apiGet<unknown>(`/api/v1/account-buckets?${p.toString()}`, signal),
      RESPONSE_KEYS.accountBuckets,
    );
  },
  /**
   * The top-level folders of an observed bucket, so a folder can be the scope.
   *
   * A scope is `(account, bucket, prefix)`, and until this existed there was no
   * way to SEE a bucket's folders before registering it, so the picker could
   * only offer whole buckets. The listing behind it is delimited rather than
   * recursive: no object key is observed, stored or returned, and a bucket of
   * ten million objects answers with the handful of prefixes a person picks
   * from.
   *
   * Every `prefix` ends in a slash, which is exactly what registration wants,
   * so what is offered here is registered unchanged. A folder that has stopped
   * appearing comes back `isPresent: false` rather than missing, because the
   * observation that it was there is still true.
   */
  listObservedBucketFolders: async (bucketName: string, companyId: string, signal?: AbortSignal) =>
    req<Data<'listObservedBucketFolders'>>(
      'observed bucket folders',
      await apiGet<unknown>(
        `/api/v1/account-buckets/${encodeURIComponent(bucketName)}/folders?companyId=${encodeURIComponent(companyId)}`,
        signal,
      ),
      RESPONSE_KEYS.observedBucketFolders,
    ),

  /**
   * Every observed bucket in the account, following the cursor to exhaustion.
   *
   * The same defect the registered catalogue had, and worse for being on the
   * one page whose job is to show you the account: `/s3` asked for no page
   * size, got the server default of fifty, and rendered them as though that
   * were the account. On this estate that is 50 of 251 - everything after
   * roughly "e" in the alphabet was unreachable by browsing, and the header
   * called the fifty "buckets observed".
   *
   * `matchedCount` is carried out separately from `items.length` because they
   * are different facts and confusing them is precisely what went wrong.
   */
  listAllAccountBuckets: async (
    companyId: string,
    q?: string,
    signal?: AbortSignal,
  ): Promise<{ items: Data<'listObservedAccountBuckets'>['items']; matchedCount: number; truncated: boolean }> => {
    const items: Data<'listObservedAccountBuckets'>['items'] = [];
    let cursor: string | undefined;
    let matchedCount = 0;
    const seen = new Set<string>();
    // Bounded runaway guard rather than a limit, and it reports itself: 200 a
    // page over 50 pages is ten thousand buckets before this can bite.
    for (let page = 0; page < 50; page++) {
      const res = await backend.listAccountBuckets(companyId, q, 200, signal, cursor);
      items.push(...res.items);
      matchedCount = res.matchedCount;
      cursor = res.nextCursor ?? undefined;
      if (!cursor) return { items, matchedCount, truncated: false };
      // A cursor that does not advance would push the same page fifty times
      // and then report the result as complete.
      if (seen.has(cursor) || res.items.length === 0) return { items, matchedCount, truncated: true };
      seen.add(cursor);
    }
    return { items, matchedCount, truncated: true };
  },

  /**
   * Whether each of a company's buckets can actually be read.
   *
   * A scope with no live content-reader grant produces a walk that never
   * reports, and until this route existed the truest thing the product could
   * say was that nothing had happened for two minutes. This is the difference
   * between "still working" and "this scope has no read grant", and it is the
   * one fact that separates them.
   *
   * The AWS role ARN is deliberately absent from the answer and is not wanted
   * here: it names a principal in Meridian's account, not a fact about the
   * tenant's data.
   */
  listContentReaderGrants: async (companyId: string, signal?: AbortSignal) =>
    req<Data<'listContentReaderGrants'>>(
      'content reader grants',
      await apiGet<unknown>(`/api/v1/buckets/content-reader-grants?companyId=${encodeURIComponent(companyId)}`, signal),
      RESPONSE_KEYS.contentReaderGrants,
    ),
  /**
   * Every reconstruction asked for on a bucket, and what became of it.
   *
   * A request row is written before anything is dispatched, so a reconstruction
   * that opened no run is answerable rather than silent. That distinction is
   * the whole point of the route: REFUSED never opened a run and carries a null
   * `reconstructionRunId`, so there is nothing to link to and the contract says
   * so in the type rather than leaving it to a reader's discipline.
   */
  listReconstructionRequests: async (bucketId: string, signal?: AbortSignal) =>
    req<Data<'listBucketReconstructionRequests'>>(
      'reconstruction requests',
      await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/reconstruction-requests`, signal),
      RESPONSE_KEYS.reconstructionRequests,
    ),
  setSweepSelection: (bucketId: string, selected: boolean) =>
    apiPut<Data<'setBucketSweepSelection'>>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/sweep-selection`, { selected }),
  getSyncDrift: async (syncRunId: string, signal?: AbortSignal) =>
    req<Data<'listSyncDrift'>>('sync drift', await apiGet<unknown>(`/api/v1/sync-runs/${encodeURIComponent(syncRunId)}/drift`, signal), RESPONSE_KEYS.syncDrift),

  /* ---- governing flow: dashboard ----
   *
   * A company-analysis run freezes a queue of tools pooled from every bucket
   * the company owns, and `CompanyAnalysisToolRecord` records no bucket
   * against an entry - so a run cannot be started for one bucket, and a
   * finished run cannot be read back as one bucket's. A bucket is the only
   * entity this product has, so there is no surface those nine readers and
   * the one writer could belong to, and a client method with no surface is a
   * capability waiting to be misused. They are gone until the contract offers
   * the same work per bucket; see docs/BACKEND_ASKS.md.
   *
   * The dashboard read stays: `entityReconstruction` carries the backend's own
   * reason why reconstruction has published nothing, which is a fact about the
   * software rather than about anyone's buckets. */
  getCompanyDashboard: async (companyId: string, signal?: AbortSignal) =>
    req<Data<'getCompanyAnalysisDashboard'>>('company dashboard', await apiGet<unknown>(`/api/v1/companies/${encodeURIComponent(companyId)}/dashboard`, signal), RESPONSE_KEYS.companyDashboard),

  /* ---- attribution (sealed-run read surface) ---- */
  getBucketToolset: async (bucketId: string, signal?: AbortSignal) =>
    req<Data<'getBucketToolset'>>('toolset', await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/toolset`, signal), RESPONSE_KEYS.toolset),
  /**
   * Attributed objects, one bounded page.
   *
   * The server defaults to 50 and mints a cursor whenever more exist. Calling
   * this with no arguments and ignoring the cursor drew fifty rows as though
   * they were the bucket.
   */
  listObjectAttributions: async (
    bucketId: string,
    opts?: { pageSize?: number; cursor?: string; tool?: string },
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams();
    if (opts?.pageSize) q.set('pageSize', String(opts.pageSize));
    if (opts?.cursor) q.set('cursor', opts.cursor);
    if (opts?.tool) q.set('tool', opts.tool);
    const qs = q.toString();
    return req<Data<'listObjectAttributions'>>('attribution objects', await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/attribution/objects${qs ? `?${qs}` : ''}`, signal), RESPONSE_KEYS.attributionObjects);
  },
  /**
   * A reviewer's correction or confirmation of one sealed attribution.
   *
   * The contract calls this the append-only human override, and it was absent
   * from the client entirely: the route existed and nothing could reach it, so
   * a person holding better knowledge than the walk had no way to record it.
   *
   * The evidence id must be the disposition's own primary evidence, which is
   * what stops an override being justified by something the walk never looked
   * at. It runs under a separate database login, so a deployment that has not
   * configured one answers 501 rather than pretending.
   */
  overrideAttribution: (
    bucketId: string,
    body: {
      sourceObjectId: string;
      attributionRunId: string;
      sourceObjectVersionId: string;
      targetKind: 'TOOL' | 'STRAY';
      toolId?: string;
      strayReason?: string;
      evidenceId: string;
      rationale: string;
      supersedesOverrideId?: string;
    },
    idempotencyKey: string,
  ) => apiPost<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/attribution/overrides`, body, idempotencyKey),

  /**
   * Carry one candidate detection to supported, on a reviewer's attestation.
   *
   * The route authorises the person; the database judges the evidence. A
   * detection short of the bar comes back 400 naming which bar failed, which
   * is why the refusal is worth rendering in full rather than as "forbidden".
   */
  promoteToolDetection: (bucketId: string, toolDetectionId: string, idempotencyKey: string) =>
    apiPost<unknown>(
      `/api/v1/buckets/${encodeURIComponent(bucketId)}/attribution/promotions`,
      { toolDetectionId },
      idempotencyKey,
    ),

  /** Stray dispositions with their closed reason kinds, one bounded page. */
  listStrayAttributions: async (
    bucketId: string,
    opts?: { pageSize?: number; cursor?: string },
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams();
    if (opts?.pageSize) q.set('pageSize', String(opts.pageSize));
    if (opts?.cursor) q.set('cursor', opts.cursor);
    const qs = q.toString();
    return req<Data<'listStrayAttributions'>>('attribution stray', await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/attribution/stray${qs ? `?${qs}` : ''}`, signal), RESPONSE_KEYS.attributionStray);
  },
  /** One chart series for a bucket. Answers AVAILABLE with a payload, or
   *  UNAVAILABLE with a reason - both are real answers and both are shown. */
  getBucketChart: async (bucketId: string, series: string, signal?: AbortSignal) =>
    req<Data<'getBucketChartSeries'>>(
      'bucket chart',
      await apiGet<unknown>(
        `/api/v1/buckets/${encodeURIComponent(bucketId)}/charts/${encodeURIComponent(series)}`,
        signal,
      ),
      RESPONSE_KEYS.bucketChart,
    ),

  /* A package composed over "a declared set of this company's buckets", with
   * a gate that is a quorum over its members, lived here. Both are the same
   * grouping under another name, and the surface that drove them is gone. */

  /** Every archive the walk enumerated, with its integrity and how much
   *  content sits inside it. A corpus whose value is locked in zips is only
   *  worth what can actually be opened. */
  listArchives: async (bucketId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/archives`, signal),

  /**
   * Where each file came from, as the tenant filed it. NOT an attribution.
   *
   * A flat list linked by `parentId`, in the tenant's own filing order, with
   * `memberCount` covering the WHOLE subtree beneath each node. Rebuilt by the
   * sync executor at the end of every sync run, so there is nothing to press.
   *
   * An empty list is a real answer: a bucket whose files sit at the root is
   * filed under nothing. A `404` is the non-disclosing not-found every
   * bucket-scoped read uses and must not be read as "no collections".
   */
  listSourceCollections: async (bucketId: string, signal?: AbortSignal) =>
    req<Data<'listSourceCollections'>>(
      'source collections',
      await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/collections`, signal),
      RESPONSE_KEYS.sourceCollections,
    ),

  /**
   * One bounded page of an archive's members.
   *
   * Keyset paged on both halves of the key: a path alone is not unique inside
   * an archive, so continuing on the path without the id can repeat or skip
   * members. Neither half was being sent, so only the first page was ever
   * reachable.
   */
  listArchiveMembers: async (
    bucketId: string,
    archiveId: string,
    opts?: { pageSize?: number; afterMemberPath?: string; afterMemberId?: string },
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams();
    if (opts?.pageSize) q.set('pageSize', String(opts.pageSize));
    if (opts?.afterMemberPath) q.set('afterMemberPath', opts.afterMemberPath);
    if (opts?.afterMemberId) q.set('afterMemberId', opts.afterMemberId);
    const qs = q.toString();
    return apiGet<unknown>(
      `/api/v1/buckets/${encodeURIComponent(bucketId)}/archives/${encodeURIComponent(archiveId)}/members${qs ? `?${qs}` : ''}`,
      signal,
    );
  },

  /** Record shapes grouped into domains by the field names they share. This is
   *  the backend's own grouping, and it is authoritative: it carries the
   *  joining keys, the tools each domain touches, and weighted agreements
   *  between shapes. */
  getDomainGraph: async (bucketId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/domain-graph`, signal),

  /** Every packaging run for a bucket, newest first. Until this existed the
   *  only way back to a run was to already hold its UUID. */
  listPackagingRuns: async (bucketId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/packaging-runs`, signal),

  /** Every export a run has built, newest first. */
  listPackagingExports: async (runId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/exports`, signal),

  /** The distinct record shapes in a bucket, grouped by their keys rather than
   *  by any rule. This is how a bucket nobody has written a rule for still
   *  becomes readable. */
  getSchemaClusters: async (bucketId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/schema-clusters`, signal),

  /** Registers the per-scope AWS role that lets a bucket be walked at all.
   *  Without it a walk cannot read content, so this is a precondition of the
   *  workflow rather than a setting. */
  registerContentReaderRole: (bucketId: string, roleArn: string, key: string) =>
    apiPost<unknown>(
      `/api/v1/buckets/${encodeURIComponent(bucketId)}/content-reader-role`,
      { roleArn },
      key,
    ),

  /** Approves an exact collector version for the walk executor. */
  approveCollectorVersion: (collectorSlug: string, collectorVersion: string, key: string) =>
    apiPost<unknown>('/api/v1/attribution/collectors/approvals', { collectorSlug, collectorVersion }, key),

  listAttributionRules: async (signal?: AbortSignal) =>
    req<Data<'listAttributionRules'>>('attribution rules', await apiGet<unknown>('/api/v1/attribution/rules', signal), RESPONSE_KEYS.attributionRules),

  /* ---- reconstruction read surface ---- */
  getWalkthrough: async (bucketId: string, signal?: AbortSignal) =>
    req<Data<'getBucketWalkthrough'>>('walkthrough', await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/walkthrough`, signal), RESPONSE_KEYS.walkthrough),
  getToolView: async (bucketId: string, tool: string, signal?: AbortSignal) =>
    req<Data<'getToolWorkspaceView'>>('tool view', await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/views/${encodeURIComponent(tool)}`, signal), RESPONSE_KEYS.toolView),
  listToolViewRows: async (bucketId: string, tool: string, section: string, signal?: AbortSignal) =>
    req<Data<'listToolWorkspaceRows'>>('tool rows', await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/views/${encodeURIComponent(tool)}/rows?section=${encodeURIComponent(section)}`, signal), RESPONSE_KEYS.toolRows),
  getGraphProjection: async (bucketId: string, lens: string, signal?: AbortSignal) =>
    req<Data<'getBucketGraphProjection'>>('graph projection', await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/graph?lens=${encodeURIComponent(lens)}`, signal), RESPONSE_KEYS.graphProjection),
  /** The next page of a graph projection. The projection returns nextCursor
   *  and this is the only route that follows it, so without this the graph
   *  silently draws its first page and nothing else. */
  expandGraphProjection: async (bucketId: string, cursor: string, signal?: AbortSignal) =>
    apiGet<unknown>(
      `/api/v1/buckets/${encodeURIComponent(bucketId)}/graph/expand?cursor=${encodeURIComponent(cursor)}`,
      signal,
    ),
  /** Every recorded path between two nodes. */
  getGraphPath: async (bucketId: string, from: string, to: string, signal?: AbortSignal) =>
    apiGet<unknown>(
      `/api/v1/buckets/${encodeURIComponent(bucketId)}/graph/path?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      signal,
    ),

  /** One decision, with the evidence references behind it. This is the answer
   *  to "why is this tool not proven yet". */
  resolveDecision: async (decisionId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/decisions/${encodeURIComponent(decisionId)}`, signal),
  /** One piece of evidence: what was observed, where, by which method. */
  resolveEvidenceRef: async (evidenceRef: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/evidence/${encodeURIComponent(evidenceRef)}`, signal),
  /** One reconstructed entity, field by field, with its provenance. */
  getEntityPreview: async (entityId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/entities/${encodeURIComponent(entityId)}/preview`, signal),

  getGraphNode: async (bucketId: string, nodeId: string, signal?: AbortSignal) =>
    req<Data<'getBucketGraphNode'>>('graph node', await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/graph/node/${encodeURIComponent(nodeId)}`, signal), RESPONSE_KEYS.graphNode),
  resolveRecord: async (recordRef: string, signal?: AbortSignal) =>
    req<Data<'resolveReconstructedRecord'>>('record', await apiGet<unknown>(`/api/v1/records/${encodeURIComponent(recordRef)}`, signal), RESPONSE_KEYS.record),
  /** One ranked page of matching records; `tool` and `kind` narrow it. */
  searchBucket: async (
    bucketId: string,
    q: string,
    opts?: { tool?: string; kind?: string; cursor?: string; pageSize?: number },
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams({ q });
    if (opts?.tool) params.set('tool', opts.tool);
    if (opts?.kind) params.set('kind', opts.kind);
    if (opts?.cursor) params.set('cursor', opts.cursor);
    // Asked for explicitly so the page's own sentence about how many more it
    // will read is true, rather than a restatement of the server's default.
    if (opts?.pageSize) params.set('pageSize', String(opts.pageSize));
    return req<Data<'searchReconstructedBucket'>>('search', await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/search?${params.toString()}`, signal), RESPONSE_KEYS.search);
  },

  /** Starts this bucket's discovery and attribution walk now, so an operator
   *  never waits for the nightly sweep on a bucket they just registered. The
   *  runtime accepts with 202; the walk stream reports what actually happens,
   *  including a refusal the dispatch cannot pre-empt. */
  dispatchWalk: (bucketId: string, idempotencyKey: string) =>
    apiPostEmpty<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/walks`, idempotencyKey),

  /* ---- packaging workflow: every step the backend proved end to end ----
   * These routes answer AnyDataEnvelope, so their payloads are returned as
   * unknown and rendered defensively. The run's own state is the authority
   * on which step is next; nothing here infers it. */
  /** Registers the run's closed population from its newest sealed walk: one
   *  record per object, each with exactly one current disposition. Until this
   *  exists there is no manifest behind the package, and the readiness gate
   *  refuses to call it complete. Idempotent. */
  registerPackagingPopulation: (runId: string, key: string) =>
    apiPostEmpty<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/population`, key),
  getPackagingToolset: (runId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/toolset`, signal),
  /** Verified against production: {included, strayDisposition:{mode,toolId?}}.
   *  MERGE_INTO requires an included target; excluding never deletes rows. */
  selectPackagingToolset: (
    runId: string,
    included: string[],
    stray: { mode: 'OWN_FOLDER' | 'MERGE_INTO' | 'EXCLUDE_RECORDED'; toolId?: string },
    key: string,
  ) =>
    apiPost<unknown>(
      `/api/v1/packaging-runs/${encodeURIComponent(runId)}/toolset/selection`,
      { included, strayDisposition: stray },
      key,
    ),
  decidePackagingCuration: (runId: string, itemId: string, decision: 'KEEP' | 'DROP', key: string) =>
    apiPost<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/curation/decisions`, { itemId, decision }, key),
  selectPackagingAxis: (runId: string, axis: string, key: string) =>
    apiPost<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/axis`, { axis }, key),
  getPackagingStructure: (runId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/structure`, signal),
  /** Per-tool folder grouping choices with real group counts and the
   *  backend's own recommendation. */
  getPackagingStructureOptions: (runId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/structure/options`, signal),
  /** Records one group's layout. Verified body: {groupPath, level3, leafOrder}. */
  sortPackagingStructure: (
    runId: string,
    groupPath: string,
    level3: string,
    leafOrder: string,
    key: string,
  ) =>
    apiPost<unknown>(
      `/api/v1/packaging-runs/${encodeURIComponent(runId)}/structure/sorts`,
      { groupPath, level3, leafOrder },
      key,
    ),
  /** Moves one tool's files to a folder you name. Verified against the Rust
   *  validator: tool and entityClass are uppercase tokens, exactly one
   *  selector field is required, and the target is a lowercase path that must
   *  end in a slash and must not begin with one. */
  mutatePackagingPlacementRule: (
    runId: string,
    rule: {
      match: { tool: string; entityClass: string; selector: Record<string, string> };
      target: string;
      basis: string;
    },
    key: string,
  ) =>
    apiPost<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/structure/rules`, rule, key),

  getPackagingCuration: (runId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/curation`, signal),
  getPackagingNameCandidates: (runId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/name-candidates`, signal),
  selectPackagingName: (runId: string, bucketName: string, packageTitle: string, key: string) =>
    apiPost<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/name`, { bucketName, packageTitle }, key),
  getPackagingDocuments: (runId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/documents`, signal),
  /** Accepting a draft is submitting its exact bytes back at the version they
   *  were drafted from, so an accept can never silently overwrite an edit. */
  acceptPackagingArtifact: (runId: string, artifactKey: string, baseVersion: number, content: string, key: string) =>
    apiPost<unknown>(
      `/api/v1/packaging-runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(artifactKey)}`,
      { baseVersion, content },
      key,
    ),
  startPackagingExport: (runId: string, kind: 'SHEET' | 'DOCUMENT', key: string) =>
    apiPost<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/exports`, { kind }, key),
  /** One export's state and recorded digest. There is no route that lists a
   *  run's exports, so the caller must hold their ids. */
  getPackagingExport: (runId: string, exportId: string, signal?: AbortSignal) =>
    apiGet<unknown>(
      `/api/v1/packaging-runs/${encodeURIComponent(runId)}/exports/${encodeURIComponent(exportId)}`,
      signal,
    ),
  /**
   * Downloads the export bytes through the API's own authenticated,
   * tenant-checked boundary. The server re-hashes on the way out and refuses
   * on a mismatch, so a failure here is an integrity failure, never a partial
   * file. No public or signed third-party URL exists, which is why this
   * streams through fetch rather than being an href.
   */
  downloadPackagingExport: async (exportId: string): Promise<{ blob: Blob; filename: string }> => {
    const res = await fetch(apiUrl(`/api/v1/exports/${encodeURIComponent(exportId)}/download`), {
      credentials: 'include',
      headers: { Accept: 'application/octet-stream' },
    });
    if (!res.ok) {
      throw new ApiErrorForGuard(
        res.status === 404 ? 'NOT_FOUND' : 'INTERNAL',
        `The export could not be downloaded (HTTP ${res.status}).`,
        res.status >= 500,
        null,
        res.status,
      );
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    return { blob: await res.blob(), filename: match?.[1] ? decodeURIComponent(match[1]) : `export-${exportId}` };
  },

  getPackagingGate: (runId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/gate`, signal),
  evaluatePackagingGate: (runId: string, key: string) =>
    apiPostEmpty<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/gate`, key),
  /** Starts the copy of this package into its own bucket. The gate is
   *  re-proven at delivery time, so yesterday's pass authorises nothing. */
  startPackagingDelivery: (runId: string, key: string) =>
    apiPostEmpty<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/delivery`, key),

  getPackagingDelivery: (runId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/delivery`, signal),

  /**
   * One page of the tree this run would deliver.
   *
   * The same derivation the delivery executor copies from, so the files
   * reviewed here are the files that ship. Keyset paged on the path:
   * `nextAfterManifestPath` is present only when the page filled, so an
   * absent cursor is the end rather than an empty page fetched to find out.
   */
  getPackagingRunManifest: (
    runId: string,
    opts?: { pageSize?: number; afterManifestPath?: string },
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams();
    if (opts?.pageSize) q.set('pageSize', String(opts.pageSize));
    if (opts?.afterManifestPath) q.set('afterManifestPath', opts.afterManifestPath);
    const qs = q.toString();
    return apiGet<unknown>(
      `/api/v1/packaging-runs/${encodeURIComponent(runId)}/manifest${qs ? `?${qs}` : ''}`,
      signal,
    );
  },

  /* ---- packaging (live; gate flipped 2026-08-02) ----
   * The step payloads are AnyDataEnvelope in the contract, so they are
   * returned as unknown and rendered defensively. Only the run itself and
   * its events carry declared schemas. */
  startPackagingRun: async (bucketId: string, idempotencyKey: string) =>
    req<PackagingRunView>(
      'packaging start',
      await apiPostEmpty<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/packaging-runs`, idempotencyKey),
      RESPONSE_KEYS.packagingRun,
    ),
  getPackagingRun: async (runId: string, signal?: AbortSignal) =>
    req<PackagingRunView>('packaging run', await apiGet<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}`, signal), RESPONSE_KEYS.packagingRun),
  getPackagingDiscovery: (runId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/discovery`, signal),
  replayPackagingEvents: (runId: string, signal?: AbortSignal) =>
    apiGet<unknown>(`/api/v1/packaging-runs/${encodeURIComponent(runId)}/events`, signal),

  /* ---- evidence + decisions ---- */
  resolveEvidenceBatch: async (ids: string[], signal?: AbortSignal) =>
    req<Data<'resolveEvidenceBatch'>>('evidence batch', await apiGet<unknown>(`/api/v1/evidence?ids=${ids.map(encodeURIComponent).join(',')}`, signal), RESPONSE_KEYS.evidenceBatch),
  getBucketFinding: async (bucketId: string, findingId: string, signal?: AbortSignal) =>
    req<Data<'getBucketFinding'>>('finding', await apiGet<unknown>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/findings/${encodeURIComponent(findingId)}`, signal), RESPONSE_KEYS.finding),
  appendReadiness: (bucketId: string, body: unknown, idempotencyKey: string) =>
    apiPost<Data<'createReadinessAssessment'>>(`/api/v1/buckets/${encodeURIComponent(bucketId)}/readiness-assessments`, body, idempotencyKey),
};

/** Client-side contract violation for the light guard above. */
class ContractViolation extends ApiErrorForGuard {
  constructor(route: string, detail: string) {
    super('CONTRACT_VIOLATION', `The ${route} response does not match the pinned contract: ${detail}`, false, null, 200);
  }
}
